/*
 * np-offer-assign.js — cookie-sticky offer/bundle splitter, ASSIGNMENT stage.
 * Built 2026-08-31.
 *
 * Loaded synchronously (no defer) as the FIRST script in <head>, so a
 * variant is known and window.NP_OFFER + dataLayer are populated before any
 * pixel script or first paint runs. Must stay small and dependency-free.
 *
 * WHAT THIS FILE OWNS: deciding the variant (A-E) and persisting it.
 * WHAT IT DOES NOT OWN: rendering. That is np-offer-render.js, which reads
 * window.NP_OFFER (set here) plus the config JSON in np-offer-config.liquid
 * and does the DOM work once the buybox section exists.
 *
 * ASSIGNMENT PRIORITY (2026-09-17 STICKY FIX — an EXISTING assignment now
 * beats an incoming URL param; before this, ?offer= re-persisted on every
 * hit, so a visitor clicking the same cold Meta ad twice got re-rolled by
 * the Voluum weights and saw a DIFFERENT PRICE than on their first visit):
 *   1. URL param ?offer=/?oc= WITH force=1 (or offer_force=1) — the explicit
 *      escape hatch. Overrides and re-persists. Case-insensitive slot.
 *   2. Existing np_offer cookie — WINS over a plain URL param, BUT ONLY when
 *      that assignment came from Voluum (np_offer_src cookie = url|forced).
 *      2026-09-28 ROOT-CAUSE FIX (sticky-funnel build, c_28ead7ab1d): this
 *      file also ran on the Landra prelander pages (theme.liquid loads it on
 *      every template), where it minted a fresh_random "A" BEFORE the visitor
 *      ever reached /click, and that organic default then beat the real
 *      Voluum cell on the PDP, so every Landra-path visitor saw control A
 *      whatever Voluum assigned. An assignment with no source cookie (legacy)
 *      or source "organic" is therefore NOT sticky against a URL param: the
 *      Voluum cell wins and re-persists with source "url". theme.liquid now
 *      also skips this file on landra templates.
 *   3. Existing np_offer_backup in localStorage (cookie was cleared but the
 *      browser still has the backup — restore it into the cookie so a
 *      cookie-clear alone doesn't reshuffle a returning visitor). Same
 *      source rule as 2.
 *   4. URL param ?offer=A..G or ?oc=A..G with NO sticky assignment (a
 *      fresh visitor arriving from Voluum) — takes the URL value and persists.
 *   5. Fresh weighted-random assignment from WEIGHTS below (source organic).
 * Sources exposed to analytics: cookie | localStorage_restore | url_param |
 * url_param_forced | fresh_random. When a plain URL param was present but an
 * existing assignment won, np_offer_url_ignored carries the ignored value.
 *
 * WEIGHTS ARE DELIBERATELY 100% CONTROL RIGHT NOW.
 * The B-E slots in np-offer-config.liquid are placeholder/dummy bundle
 * content (brief item 3: "the REAL offers arrive from a parallel design
 * task"). Splitting real, live organic traffic into dummy content before
 * that lands would show fake bundle tiles to real customers. The full
 * mechanism is live and testable right now via ?offer=B..E (manual QA /
 * the future Voluum hook) — only the ORGANIC weighted-random default is
 * held at 100% A until real offers are wired into the config. To turn on
 * a live weighted test across all visitors once real content lands, this
 * WEIGHTS object is the one line to change — no other code changes needed.
 */
(function () {
  'use strict';

  // 2026-09-01: slots F + G added (offer-variation round — F = Buy 2 Get 2
  // Free 4-pack, G = $49-anchor Buy 2 Get 1 Free). Voluum assigns the cell via
  // ?offer=; the organic default below stays 100% control on purpose.
  var SLOTS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
  var WEIGHTS = { A: 100, B: 0, C: 0, D: 0, E: 0, F: 0, G: 0 }; // sums to 100; see note above
  var COOKIE_NAME = 'np_offer';
  var SRC_COOKIE = 'np_offer_src';   // url | forced | organic (2026-09-28)
  var LS_KEY = 'np_offer_backup';
  var LS_SRC_KEY = 'np_offer_src_backup';
  var COOKIE_DAYS = 30;

  function getParam(name) {
    try {
      var m = new RegExp('[?&]' + name + '=([^&#]*)').exec(window.location.search);
      return m ? decodeURIComponent(m[1]).toUpperCase() : null;
    } catch (e) {
      return null;
    }
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    var secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; expires=' + d.toUTCString() +
      '; path=/; SameSite=Lax' + secure;
  }

  function getLocal(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }

  function setLocal(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* storage disabled — cookie is still primary */ }
  }

  function isValidSlot(v) {
    return typeof v === 'string' && SLOTS.indexOf(v) !== -1;
  }

  function weightedRandom() {
    var total = 0;
    for (var i = 0; i < SLOTS.length; i++) total += WEIGHTS[SLOTS[i]] || 0;
    if (total <= 0) return 'A';
    var r = Math.random() * total;
    var acc = 0;
    for (var j = 0; j < SLOTS.length; j++) {
      acc += WEIGHTS[SLOTS[j]] || 0;
      if (r < acc) return SLOTS[j];
    }
    return 'A';
  }

  function persist(slot, src) {
    setCookie(COOKIE_NAME, slot, COOKIE_DAYS);
    setLocal(LS_KEY, slot);
    setCookie(SRC_COOKIE, src, COOKIE_DAYS);
    setLocal(LS_SRC_KEY, src);
  }

  // An assignment is only sticky against a plain URL param when Voluum made it.
  function isSticky(src) {
    return src === 'url' || src === 'forced';
  }

  function assign() {
    var urlOffer = getParam('offer') || getParam('oc');
    var urlValid = isValidSlot(urlOffer);
    var forced = getParam('force') === '1' || getParam('offer_force') === '1';

    // 1. Forced URL param — explicit override, re-persists (QA / deliberate
    //    re-cell). The ONLY path that can change an existing assignment.
    if (urlValid && forced) {
      persist(urlOffer, 'forced');
      return { slot: urlOffer, source: 'url_param_forced' };
    }

    // 2. Existing Voluum-made cookie beats a plain URL param (sticky price on
    //    repeat click). A legacy/organic assignment yields to a URL param.
    var cookieOffer = getCookie(COOKIE_NAME);
    var cookieSrc = getCookie(SRC_COOKIE) || getLocal(LS_SRC_KEY) || '';
    if (isValidSlot(cookieOffer) && (isSticky(cookieSrc) || !urlValid)) {
      // Keep localStorage backup in sync in case it fell out of step.
      if (getLocal(LS_KEY) !== cookieOffer) setLocal(LS_KEY, cookieOffer);
      return { slot: cookieOffer, source: 'cookie', urlIgnored: urlValid ? urlOffer : null };
    }

    // 3. Cookie cleared but localStorage backup survives — restore it. Same
    //    source rule: only a Voluum-made backup beats a plain URL param.
    var backup = getLocal(LS_KEY);
    var backupSrc = getLocal(LS_SRC_KEY) || '';
    if (isValidSlot(backup) && (isSticky(backupSrc) || !urlValid)) {
      setCookie(COOKIE_NAME, backup, COOKIE_DAYS);
      if (backupSrc) setCookie(SRC_COOKIE, backupSrc, COOKIE_DAYS);
      return { slot: backup, source: 'localStorage_restore', urlIgnored: urlValid ? urlOffer : null };
    }

    // 4. URL param with no sticky assignment — fresh visitor from Voluum (or a
    //    legacy/organic assignment being replaced by the real Voluum cell).
    if (urlValid) {
      persist(urlOffer, 'url');
      return { slot: urlOffer, source: 'url_param' };
    }

    // 5. Fresh visitor, no param — weighted random (organic default).
    var fresh = weightedRandom();
    persist(fresh, 'organic');
    return { slot: fresh, source: 'fresh_random' };
  }

  var result = assign();

  // Analytics exposure (brief item 5) — set BEFORE any pixel script has a
  // chance to run, since this file is the first synchronous script in <head>.
  window.NP_OFFER = result.slot;
  window.NP_OFFER_SOURCE = result.source;
  window.NP_OFFER_URL_IGNORED = result.urlIgnored || null;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'np_offer_assigned',
    np_offer: result.slot,
    np_offer_source: result.source,
    np_offer_url_ignored: result.urlIgnored || null
  });

  // Let np-offer-render.js (deferred, runs after the buybox DOM exists)
  // know assignment already happened, in case it loads before this fires
  // its own readiness — cheap belt-and-suspenders, costs nothing.
  document.dispatchEvent(new CustomEvent('np:offer-assigned', { detail: result }));
})();
